/**
 * Facet sets for the entities whose dimensions are few — people,
 * municipalities and subsidies. No taxonomy, but real counts.
 */

import type { FacetSet } from './types';

/* ------------------------------------------------------------------ people */

const PERSON_FLAGS = [
  { key: 'mayor', label: 'кметове', countsFrom: 'roles', countValue: 'mayor' },
  // Separate from кметове on purpose: a councillor votes the budget and a
  // mayor spends it, and one filter for both would hide which a person is.
  { key: 'councillor', label: 'общински съветници', countsFrom: 'roles', countValue: 'councillor' },
  { key: 'villageMayor', label: 'кметове на кметства', countsFrom: 'roles', countValue: 'villageMayor' },
  { key: 'owner', label: 'собственици на фирми', countsFrom: 'roles', countValue: 'owner' },
  { key: 'manager', label: 'управители', countsFrom: 'roles', countValue: 'manager' },
] as const;

/* Thresholds on company_links, which counts the distinct companies a person
   owns or manages. This is the control that maps a network of related
   businesses — one person sitting across a dozen of them is the lead — and
   it went missing in the React port until it was noticed. */
const LINK_BANDS = [
  { value: '2', label: '2 и повече фирми' },
  { value: '3', label: '3 и повече' },
  { value: '5', label: '5 и повече' },
  { value: '10', label: '10 и повече' },
] as const;

export type PersonState = {
  search: string; mayor: boolean; councillor: boolean; villageMayor: boolean;
  owner: boolean; manager: boolean; links: string;
};

export function personFacetSet(prefix = 'p.'): FacetSet<PersonState, Record<string, unknown>, Record<string, unknown>> {
  const empty = (): PersonState => ({
    search: '', mayor: false, councillor: false, villageMayor: false,
    owner: false, manager: false, links: '',
  });
  return {
    prefix,
    groups: [
      { key: 'flags', title: 'Роля', kind: 'flags', items: PERSON_FLAGS, counts: 'roles' },
      {
        key: 'links',
        title: 'Свързан с фирми',
        kind: 'threshold',
        counts: 'linkBands',
        options: LINK_BANDS,
      },
    ],
    emptyState: empty,
    fromParams: (get) => ({
      search: get('q') ?? '',
      mayor: get('mayor') === '1',
      councillor: get('councillor') === '1',
      villageMayor: get('villageMayor') === '1',
      owner: get('owner') === '1',
      manager: get('manager') === '1',
      links: get('links') ?? '',
    }),
    toParams: (state) => ({
      q: state.search || null,
      mayor: state.mayor ? '1' : null,
      councillor: state.councillor ? '1' : null,
      villageMayor: state.villageMayor ? '1' : null,
      owner: state.owner ? '1' : null,
      manager: state.manager ? '1' : null,
      links: state.links || null,
    }),
    activeCount: (state) => [state.mayor, state.councillor, state.villageMayor,
      state.owner, state.manager].filter(Boolean).length + (state.links ? 1 : 0),
    toFilter: (state) => ({
      search: state.search || null,
      isMayor: state.mayor ? true : null,
      isCouncillor: state.councillor ? true : null,
      isVillageMayor: state.villageMayor ? true : null,
      isOwner: state.owner ? true : null,
      isManager: state.manager ? true : null,
      minCompanies: state.links ? Number(state.links) : null,
    }),
    toWhere: (state) => {
      const clauses: Record<string, unknown>[] = [];
      if (state.search) clauses.push({ name_CONTAINS: state.search });
      if (state.mayor) clauses.push({ mayorOfAggregate: { count_GT: 0 } });
      if (state.councillor) {
        clauses.push({ positionsConnection_SOME: { edge: { role: 'общински съветник' } } });
      }
      if (state.villageMayor) {
        clauses.push({ positionsConnection_SOME: { edge: { NOT: { role: 'общински съветник' } } } });
      }
      if (state.owner) clauses.push({ ownedCompaniesAggregate: { count_GT: 0 } });
      if (state.manager) clauses.push({ managedCompaniesAggregate: { count_GT: 0 } });
      const links = Number(state.links);
      if (Number.isFinite(links) && links > 0) clauses.push({ company_links_GTE: links });
      if (!clauses.length) return null;
      return clauses.length === 1 ? clauses[0]! : { AND: clauses };
    },
  };
}

/* ---------------------------------------------------------- municipalities */

const MUNI_FLAGS = [
  { key: 'procuring', label: 'с възложени поръчки', countsFrom: 'flags', countValue: 'tenders' },
] as const;

export type MunicipalityState = { search: string; procuring: boolean; provinces: string[] };

export function municipalityFacetSet(prefix = 'm.'): FacetSet<MunicipalityState, Record<string, unknown>, Record<string, unknown>> {
  const empty = (): MunicipalityState => ({ search: '', procuring: false, provinces: [] });
  return {
    prefix,
    groups: [
      { key: 'provinces', title: 'Област', kind: 'check', counts: 'provinces' },
      { key: 'flags', title: 'Филтри', kind: 'flags', items: MUNI_FLAGS, counts: 'flags' },
    ],
    emptyState: empty,
    fromParams: (get, getAll) => ({
      search: get('q') ?? '',
      procuring: get('procuring') === '1',
      provinces: getAll('provinces'),
    }),
    toParams: (state) => ({
      q: state.search || null,
      procuring: state.procuring ? '1' : null,
      provinces: state.provinces.length ? state.provinces : null,
    }),
    activeCount: (state) => (state.procuring ? 1 : 0) + (state.provinces.length ? 1 : 0),
    toFilter: (state) => ({
      search: state.search || null,
      provinces: state.provinces,
      hasTenders: state.procuring ? true : null,
    }),
    toWhere: (state) => {
      const clauses: Record<string, unknown>[] = [];
      if (state.search) clauses.push({ name_CONTAINS: state.search });
      if (state.provinces.length) clauses.push({ province_IN: state.provinces });
      if (state.procuring) clauses.push({ tendersAggregate: { count_GT: 0 } });
      if (!clauses.length) return null;
      return clauses.length === 1 ? clauses[0]! : { AND: clauses };
    },
  };
}

/* --------------------------------------------------------------- subsidies */

export type SubsidyState = { search: string; years: string[]; sources: string[] };

export interface SubsidyScope { beneficiaryUics?: string[] }

export function subsidyFacetSet(prefix = 's.', scope: SubsidyScope = {}): FacetSet<SubsidyState, Record<string, unknown>, Record<string, unknown>> {
  const empty = (): SubsidyState => ({ search: '', years: [], sources: [] });
  return {
    prefix,
    groups: [
      { key: 'years', title: 'Година', kind: 'check', counts: 'years' },
      { key: 'sources', title: 'Източник', kind: 'check', counts: 'sources' },
    ],
    emptyState: empty,
    fromParams: (get, getAll) => ({
      search: get('q') ?? '',
      years: getAll('years'),
      sources: getAll('sources'),
    }),
    toParams: (state) => ({
      q: state.search || null,
      years: state.years.length ? state.years : null,
      sources: state.sources.length ? state.sources : null,
    }),
    activeCount: (state) => (state.years.length ? 1 : 0) + (state.sources.length ? 1 : 0),
    toFilter: (state) => ({
      search: state.search || null,
      years: state.years.map(Number).filter(Number.isFinite),
      sources: state.sources,
      ...(scope.beneficiaryUics?.length ? { beneficiaryUics: scope.beneficiaryUics } : {}),
    }),
    toWhere: (state) => {
      const clauses: Record<string, unknown>[] = [];
      if (scope.beneficiaryUics?.length) {
        clauses.push({ beneficiaries_SOME: { uic_IN: scope.beneficiaryUics } });
      }
      if (state.search) {
        clauses.push({
          OR: [
            { measure_CONTAINS: state.search },
            { project_title_CONTAINS: state.search },
          ],
        });
      }
      if (state.years.length) clauses.push({ year_IN: state.years.map(Number) });
      if (state.sources.length) clauses.push({ source_IN: state.sources });
      if (!clauses.length) return null;
      return clauses.length === 1 ? clauses[0]! : { AND: clauses };
    },
  };
}
